/**
 * Il testo che una persona legge nella propria lingua, e il modo in cui si dichiara
 * quando quella lingua non c'è.
 *
 * ## Il difetto
 *
 * Una comunicazione ha **un** titolo e **un** corpo. I destinatari sono una tabella a
 * parte, e la lingua di ciascuno sta sulla sua riga in anagrafica (K73). Fra le due cose
 * non c'era niente: né la bacheca né la coda della spinta leggevano `language`, quindi
 * ogni destinatario riceveva l'italiano — e poi lo apriva, e `readAt` si valorizzava.
 * Questo è il punto che rende il difetto peggiore di «non è tradotto»: un magazziniere
 * cinese che apre una circolare che non può leggere **risulta informato**, e la casella
 * si chiude. Una comunicazione consegnata che non è arrivata è peggio di una non
 * inviata, perché toglie la domanda invece di rispondere.
 *
 * ## Le quattro regole della decisione del 2026-08-05 (K75)
 *
 * **(a) L'italiano è l'originale, le altre due sono derivate.** Se l'originale cambia, le
 * derivate **scadono**: una traduzione rimasta indietro racconta una cosa vecchia con
 * l'aria di essere quella nuova, ed è peggio di una assente. Qui la regola diventa
 * {@link improntaTesto} più il confronto in {@link scegliTesto}: una traduzione fatta da
 * un testo diverso da quello di adesso **non si mostra**, punto, anche se la coda non ha
 * ancora fatto in tempo a rifarla.
 *
 * **(b) Il testo automatico si dichiara.** {@link TestoPerChiLegge.origine} esce con il
 * testo e arriva fino allo schermo. Non è una finezza: è quel che permette a chi legge di
 * pesare una frase strana, e a chi corregge di sapere che cosa sta guardando.
 *
 * **(c) La correzione a mano vince.** Una traduzione con origine `MANO` la coda non la
 * ritocca finché l'originale non cambia. Senza questa distinzione non esisterebbe modo di
 * migliorare le traduzioni: si ritradurrebbe, e la correzione di ieri sparirebbe.
 *
 * **(d) La traduzione non blocca l'invio.** Qui dentro non c'è niente che parli con un
 * traduttore: sono tipi e funzioni pure. La comunicazione parte in italiano e le versioni
 * arrivano quando sono pronte — un traduttore lento non deve poter trattenere un avviso
 * urgente.
 *
 * ## Perché il ripiego ha un motivo e non è solo «italiano»
 *
 * K74 regola (a) dice che quando la traduzione manca si mostra l'italiano **dichiarando
 * che è un ripiego**, mai il codice. {@link MotivoRipiego} spinge quella dichiarazione
 * un passo più in là: «non è ancora tradotta», «è scaduta» e «non si è riusciti a
 * tradurla» sono tre fatti diversi per chi amministra, e indistinguibili se il campo
 * fosse un booleano. Il primo passa da solo, il secondo si ripara al prossimo giro, il
 * terzo vuole qualcuno che guardi. È la stessa ragione per cui la coda della spinta
 * distingue `no_registered_device` da `device_stale` invece di scrivere «non consegnata».
 */
import { LINGUA_PREDEFINITA, LINGUE } from './lingua.js';
/**
 * Le lingue in cui si **traduce**, cioè tutte tranne quella in cui si scrive.
 *
 * Ricavate, non ricopiate: l'italiano non è una delle traduzioni, è il contenuto da cui
 * si traduce, e scrivere `['en', 'zh']` a mano qui sarebbe la quarta copia dell'insieme
 * chiuso che `lingua.ts` esiste per togliere (K37). Una quarta lingua aggiunta là dentro
 * compare qui da sola.
 */
export const LINGUE_TRADOTTE = LINGUE.filter((l) => l !== LINGUA_PREDEFINITA);
/**
 * `x` è una lingua in cui si traduce.
 *
 * Serve al confine: la lingua di una riga di traduzione arriva dalla banca dati come
 * stringa, e `it` in quella colonna vorrebbe dire che l'originale si è procurato un
 * concorrente. Il vincolo `CHECK` della migrazione dice la stessa cosa al livello sotto;
 * questa è la porta di sopra.
 */
export function eLinguaTradotta(x) {
    return typeof x === 'string' && LINGUE_TRADOTTE.includes(x);
}
/**
 * L'impronta del testo da cui una traduzione è stata fatta.
 *
 * ## A che serve
 *
 * A rispondere a una domanda sola: «questa traduzione viene dal testo che c'è adesso?».
 * Se no, è scaduta, e la regola (a) dice che non si mostra. Il confronto avviene in
 * {@link scegliTesto}, cioè **in lettura**: la coda che rifà le traduzioni scadute è la
 * riparazione, non la difesa. Se fosse solo la coda a difendere, una traduzione scaduta
 * resterebbe a schermo per tutto il tempo che passa fra la modifica e il giro successivo.
 *
 * ## Perché non è una funzione crittografica
 *
 * Perché non ce n'è bisogno e costerebbe cara. `node:crypto` non esiste sul telefono, e
 * questo pacchetto lo importano anche la app e il pannello: un `import` di Node in cima a
 * questo file romperebbe il pacchetto della app. Scrivere qui uno SHA-256 a mano
 * significherebbe mantenere ottanta righe di aritmetica a 32 bit per proteggersi da un
 * avversario che non c'è — nessuno **attacca** l'impronta di una circolare, e chi potesse
 * scriverla potrebbe già scrivere la traduzione.
 *
 * Quello che serve è che due testi *diversi* diano impronte diverse, e per questo bastano
 * due FNV-1a a 32 bit con basi diverse sugli stessi byte UTF-8 — 64 bit in tutto — con
 * davanti le due lunghezze in caratteri. Le lunghezze non sono decorazione: sono un
 * discriminante indipendente dall'aritmetica dell'hash, quindi una modifica che
 * collidesse sui 64 bit dovrebbe **anche** lasciare identiche entrambe le lunghezze. E
 * l'impronta resta leggibile a occhio in una riga di tabella, il che il giorno che
 * qualcuno debuga «perché questa non scade» vale più della robustezza in più.
 *
 * Il separatore `|` fra titolo e corpo, e il `-` per il corpo assente, servono a
 * distinguere i casi che altrimenti si confonderebbero: titolo `ab` con corpo `c` e
 * titolo `a` con corpo `bc` non sono lo stesso testo, e concatenarli e basta li renderebbe
 * la stessa impronta.
 */
export function improntaTesto(testo) {
    const corpo = testo.corpo ?? '';
    const unito = `${testo.titolo}|${testo.corpo === null ? '-' : corpo}`;
    const byte = inUtf8(unito);
    return (`${testo.titolo.length}.${testo.corpo === null ? '-' : corpo.length}.` +
        `${fnv1a(byte, 0x811c9dc5)}${fnv1a(byte, 0x01000193)}`);
}
/**
 * Il testo nei suoi byte UTF-8, scritto a mano.
 *
 * `TextEncoder` sarebbe una riga sola e non si può usare: questo pacchetto compila con
 * `lib: ["ES2022"]` e basta — niente `DOM`, niente `@types/node` — perché lo importa anche
 * la app sul telefono, e allargare la libreria qui vorrebbe dire far compilare, dentro il
 * codice condiviso, chiamate a `document` o a `process` che sul telefono non esistono. Il
 * costo di quella disciplina è questa funzione; è un costo che si paga una volta.
 *
 * `for...of` su una stringa scorre i **punti di codice**, non le unità a 16 bit: un
 * ideogramma fuori dal piano base — o un'emoji — arriva intero invece che spezzato in due
 * surrogati, che è precisamente il caso in cui una codifica scritta a mano sbaglia. Le
 * comunicazioni si traducono in cinese, quindi non è un caso di scuola.
 */
function inUtf8(testo) {
    const byte = [];
    for (const carattere of testo) {
        // `for...of` non produce mai una stringa vuota, quindi `codePointAt(0)` non è mai
        // `undefined`; il `?? 0` è per il compilatore, non per un caso che si dia.
        const c = carattere.codePointAt(0) ?? 0;
        if (c < 0x80) {
            byte.push(c);
        }
        else if (c < 0x800) {
            byte.push(0xc0 | (c >> 6), 0x80 | (c & 0x3f));
        }
        else if (c < 0x10000) {
            byte.push(0xe0 | (c >> 12), 0x80 | ((c >> 6) & 0x3f), 0x80 | (c & 0x3f));
        }
        else {
            byte.push(0xf0 | (c >> 18), 0x80 | ((c >> 12) & 0x3f), 0x80 | ((c >> 6) & 0x3f), 0x80 | (c & 0x3f));
        }
    }
    return Uint8Array.from(byte);
}
/**
 * FNV-1a a 32 bit, in esadecimale su otto cifre.
 *
 * `Math.imul` e non `*`: il prodotto di due interi a 32 bit esce dai 53 bit esatti di un
 * `number`, e senza `imul` le cifre basse — cioè proprio quelle che l'hash sta mescolando
 * — verrebbero arrotondate via. `>>> 0` riporta il risultato fra gli interi senza segno
 * dopo lo XOR, che in JavaScript lavora con segno.
 */
function fnv1a(byte, base) {
    let h = base >>> 0;
    for (const b of byte) {
        h = (h ^ b) >>> 0;
        h = Math.imul(h, 0x01000193) >>> 0;
    }
    return h.toString(16).padStart(8, '0');
}
/**
 * Quale traduzione mostrare a chi legge in `lingua`, o perché nessuna.
 *
 * L'ordine dei controlli è l'ordine in cui i fatti smettono di essere veri:
 *
 *  1. chi legge in italiano legge l'originale, e non c'è niente da decidere;
 *  2. la riga non c'è: nessuno ha ancora provato a tradurre — è il caso di una
 *    comunicazione appena partita, e la regola (d) dice che è normale;
 *  3. la riga c'è ma viene da un testo diverso: **scaduta**, e questo si controlla prima
 *    dello stato, perché una riga `PRONTA` fatta dal testo di ieri è esattamente il caso
 *    che la regola (a) chiama peggiore di una traduzione assente;
 *  4. la riga è ancora in coda o ha rinunciato: due motivi diversi, nessuno dei due
 *    mostrabile.
 */
export function scegliTraduzione(improntaOriginale, lingua, traduzioni) {
    if (lingua === LINGUA_PREDEFINITA)
        return { sua: null, ripiego: null };
    const sua = traduzioni.find((t) => t.lingua === lingua);
    if (!sua)
        return { sua: null, ripiego: 'NON_ANCORA_TRADOTTA' };
    if (sua.improntaSorgente !== improntaOriginale) {
        return { sua: null, ripiego: 'TRADUZIONE_SCADUTA' };
    }
    if (sua.stato === 'IN_ATTESA')
        return { sua: null, ripiego: 'NON_ANCORA_TRADOTTA' };
    if (sua.stato === 'FALLITA')
        return { sua: null, ripiego: 'TRADUZIONE_NON_RIUSCITA' };
    return { sua, ripiego: null };
}
/**
 * Il testo da mettere davanti a chi legge, con scritto che cos'è.
 *
 * **Una funzione sola per la bacheca e per la spinta.** Sono due percorsi che leggono le
 * stesse righe per due mestieri diversi — uno riempie una schermata, l'altro compone la
 * notifica di sistema del telefono — e la regola su quando una traduzione si può mostrare
 * è la stessa. Scritta due volte, divergerebbe: è §60, «una misura ricopiata non sbaglia,
 * invecchia». La prova che qui serva davvero è che la seconda copia avrebbe con ogni
 * probabilità dimenticato il confronto delle impronte, che è la parte silenziosa.
 *
 * **Quale sia la traduzione buona non lo decide qui.** Lo decide {@link scegliTraduzione},
 * che è la stessa regola vista senza i testi: qui si aggiunge solo il passo che quella non
 * può fare, cioè *comporre* — mettere insieme il testo scelto, oppure ripiegare
 * sull'italiano dichiarando perché. La divisione conta perché il ripiego, da K75 in avanti,
 * si scrive anche in tabella, e chi lo scrive non sta disegnando niente.
 *
 * `corpo` vuoto insieme a un titolo tradotto è un caso vero e passa: una comunicazione
 * senza corpo esiste, e `null` nella traduzione vuol dire «non c'era niente da tradurre»,
 * non «la traduzione è a metà».
 */
export function scegliTesto(originale, improntaOriginale, lingua, traduzioni) {
    const scelta = scegliTraduzione(improntaOriginale, lingua, traduzioni);
    if (!scelta.sua) {
        return {
            titolo: originale.titolo,
            corpo: originale.corpo,
            lingua: LINGUA_PREDEFINITA,
            origine: null,
            ripiego: scelta.ripiego,
        };
    }
    return {
        titolo: scelta.sua.titolo,
        corpo: scelta.sua.corpo,
        lingua: scelta.sua.lingua,
        origine: scelta.sua.origine,
        ripiego: null,
    };
}
/**
 * Perché una traduzione non si è potuta fare, in una frase che si legge.
 *
 * ## Perché la tabella sta qui e non nel pannello
 *
 * Perché i codici li scrive il server e le frasi le legge chi amministra, e sono due file
 * che non si conoscono. Una tabella di frasi copiata nel pannello non sbaglierebbe il
 * giorno che nasce: sbaglierebbe il giorno che il traduttore impara un motivo nuovo, e a
 * chi guarda comparirebbe `translate_unreadable` crudo — cioè la schermata che deve dire
 * *perché* smette di dirlo proprio quando succede qualcosa che non era previsto. È §60.
 *
 * Standoci una volta sola, il legame è il compilatore: {@link CodiceMotivoTraduzione} è il
 * tipo con cui il traduttore dichiara i suoi fallimenti, quindi un motivo nuovo **non
 * compila** finché non ha la sua frase qui dentro. Non è un promemoria, è un cancello.
 *
 * ## Perché un codice sconosciuto si mostra invece di essere nascosto
 *
 * Perché la schermata non deve mai dire meno di quello che sa. Un motivo che non è in
 * tabella arriva da una versione del server più nuova del pannello, ed è **esattamente** il
 * caso in cui chi guarda ha bisogno di leggere la parola esatta da mettere in una
 * segnalazione. Una frase generica al suo posto — «errore di traduzione» — cancellerebbe
 * l'unica informazione utile rimasta, che è §61 applicato a una schermata invece che a un
 * cancello: se non ha di che dire, lo dice, non tace.
 */
export const MOTIVI_TRADUZIONE = {
    translator_disabled: 'Il traduttore è spento: «translation.enabled» è falsa nelle impostazioni.',
    translator_not_configured: 'Il traduttore non ha un indirizzo: «translation.endpoint» è vuota nelle impostazioni.',
    text_too_long: 'Il testo supera il massimo di caratteri per una traduzione automatica ' +
        '(«translation.max_characters»). Si alza la manopola, oppure si traduce a mano.',
    translate_timeout: 'Il traduttore non ha risposto entro il tempo massimo.',
    translate_unreachable: 'Il traduttore non risponde: il servizio è spento o irraggiungibile.',
    translate_unreadable: 'Il traduttore ha risposto qualcosa che non è una traduzione leggibile.',
    translate_empty: 'Il traduttore ha risposto senza testo. La riga è chiusa apposta invece di salvare ' +
        'una traduzione vuota, che a schermo sarebbe una comunicazione senza titolo.',
    translate_error: 'Il traduttore ha sollevato un errore mentre traduceva.',
    language_not_translatable: 'Sulla riga c’è una lingua in cui il sistema non traduce. Non dovrebbe potersi ' +
        'scrivere: se è successo, va segnalato.',
    body_not_reassembled: 'La traduzione è tornata indietro in una forma che non si è potuta rimettere dentro la ' +
        'comunicazione: un pezzo in più o in meno, oppure un corpo che rimontato non si rilegge ' +
        'più come è stato scritto. Sarebbe uscita una circolare con la formattazione o un ' +
        'indirizzo diversi da quelli spediti, e la riga è chiusa apposta invece di salvarla. Il ' +
        'registro del server dice quale pezzo non tornava.',
    body_is_comunicato: 'Motivo storico: c’è stato un tempo in cui un corpo in formato COMUNICATO non si ' +
        'traduceva affatto, perché la macchina non sapeva rimontarlo. Adesso lo sa, e traduce i ' +
        'soli pezzi di testo. Le righe che portano ancora questo motivo sono di allora: si ' +
        'rimettono in coda col bottone «Riprova le non riuscite», e non lo fa nessuno al posto ' +
        'di chi guarda.',
};
/**
 * Quali fallimenti ha senso riprovare, e perché — cioè dove finisce «definitivo».
 *
 * ## Il difetto che questa tabella chiude (K230)
 *
 * `definitivo`, sul traduttore, vuol dire una cosa sola e ristretta: **non riprovare dentro
 * questo giro di coda**. Non riprovare mille volte al minuto contro una porta che nessuno ha
 * aperto è giusto, e resta giusto. Quello che non era vero è la conseguenza che il codice si
 * portava dietro senza dirlo: una riga chiusa così non la ripescava **più nessuno, mai**.
 * `claimDue` pesca solo le `IN_ATTESA`, e `pianificaTraduzioni` riporta in attesa soltanto
 * quando il testo italiano cambia — un testo che non cambia più è una riga morta per sempre.
 *
 * Due frasi promettevano il contrario. Una in un commento (`libretranslate.traduttore.ts`:
 * «riacceso l'interruttore, le righe rimaste indietro si rimettono in coda»), l'altra **in
 * questa stessa tabella**, sotto `body_is_comunicato`, cioè una frase che una persona legge
 * sullo schermo e che le dice di premere un bottone che non esisteva. Misurato sul collaudo:
 * 872 righe `fallita`, tutte `translator_disabled`, tutte con l'impronta ancora coerente col
 * testo di adesso — cioè nessuna di loro sarebbe mai stata ripescata da niente.
 *
 * ## Perché la decisione sta qui e non nel servizio
 *
 * Perché è la stessa ragione di {@link MOTIVI_TRADUZIONE}: il codice lo scrive il server, la
 * conseguenza la subisce chi amministra, e la schermata deve poter dire **prima di premere**
 * quante righe si muoveranno. Con la tabella di là il pannello tirerebbe a indovinare.
 *
 * Ed è un `Record<MotivoTraduzione, …>` e non un elenco: così un motivo nuovo non compila
 * finché qualcuno non ha deciso da che parte sta. Un elenco dei riprovabili avrebbe lasciato
 * il motivo nuovo fuori **in silenzio**, cioè avrebbe rifatto lo stesso difetto in piccolo —
 * la lezione di P32, che un `keyof typeof` protegge dalle voci sbagliate e non da quelle che
 * mancano.
 */
export const RIMETTIBILE_IN_CODA = {
    translator_disabled: {
        rimettibile: true,
        perche: 'la causa è un interruttore, e un interruttore si riaccende',
    },
    translator_not_configured: {
        rimettibile: true,
        perche: 'la causa è un indirizzo vuoto in un’impostazione, e si riempie',
    },
    text_too_long: {
        rimettibile: true,
        perche: 'il testo supera una manopola, non un limite del mondo: alzata «translation.max_characters», ' +
            'la stessa riga passa',
    },
    translate_timeout: {
        rimettibile: true,
        perche: 'il motore era lento in quel momento; adesso può non esserlo',
    },
    translate_unreachable: {
        rimettibile: true,
        perche: 'il servizio era spento o irraggiungibile in quel momento',
    },
    translate_unreadable: {
        rimettibile: true,
        perche: 'ha risposto male una volta, e non è una proprietà del testo: un motore aggiornato può ' +
            'rispondere bene alla stessa domanda',
    },
    translate_empty: {
        rimettibile: true,
        perche: 'ha risposto vuoto una volta, per la stessa ragione di qui sopra',
    },
    translate_error: {
        rimettibile: true,
        perche: 'ha sollevato un errore in quel momento; il registro del server dice quale',
    },
    language_not_translatable: {
        rimettibile: false,
        perche: 'sulla riga c’è una lingua in cui il sistema non traduce, e riprovare non cambia la ' +
            'lingua: nessun numero di tentativi insegna una lingua alla macchina. Questa vuole una ' +
            'persona, ed è anche l’unico motivo che non dovrebbe potersi scrivere affatto',
    },
    body_not_reassembled: {
        rimettibile: true,
        perche: 'il rimontaggio è fallito su **quella** risposta, non su quel testo: una risposta diversa ' +
            'può rimontarsi. Può anche fallire di nuovo allo stesso modo, e allora la riga si richiude ' +
            'con lo stesso motivo — che è il verso giusto in cui sbagliare',
    },
    body_is_comunicato: {
        rimettibile: true,
        perche: 'è il motivo storico di quando la macchina non sapeva rimontare un COMUNICATO. Adesso lo ' +
            'sa: queste righe non sono fallite, sono vecchie',
    },
};
/**
 * Se una riga chiusa con questo motivo valga la pena di rimetterla in coda.
 *
 * I motivi `translate_http_<n>` non sono nella tabella — il numero lo decide il motore — e
 * sono riprovabili per costruzione: uno stato HTTP è un fatto del servizio in quell'istante,
 * non del testo. Un codice che non si conosce affatto torna `true` per la stessa ragione per
 * cui {@link fraseMotivoTraduzione} mostra il codice crudo invece di nasconderlo: arriva da una
 * versione che qui non si conosce, e il danno di riprovare una riga di troppo è un tentativo
 * sprecato, mentre il danno di non riprovarla è una riga morta di cui nessuno saprà mai.
 */
export function eRimettibileInCoda(codice) {
    if (codice === null || codice === undefined || codice === '')
        return true;
    if (codice in RIMETTIBILE_IN_CODA) {
        return RIMETTIBILE_IN_CODA[codice].rimettibile;
    }
    return true;
}
/** I motivi che non si rimettono in coda, per chi deve scriverne l'elenco in una query. */
export const MOTIVI_NON_RIMETTIBILI = Object.keys(RIMETTIBILE_IN_CODA).filter((m) => !RIMETTIBILE_IN_CODA[m].rimettibile);
/**
 * La frase per un codice, o il codice stesso quando non se ne conosce una.
 *
 * `null` entra e esce: una riga senza motivo è la norma — le traduzioni riuscite e quelle
 * ancora in coda non ne hanno — e obbligare chi chiama a un `if` prima di chiedere
 * significherebbe scriverlo in ogni schermata che mostra queste righe.
 */
export function fraseMotivoTraduzione(codice) {
    if (codice === null || codice === undefined || codice === '')
        return null;
    if (codice in MOTIVI_TRADUZIONE)
        return MOTIVI_TRADUZIONE[codice];
    // Lo stato HTTP si legge, non si elenca. Il 4xx è nostro — quasi sempre il modello di
    // quella coppia di lingue che non è installato sul contenitore — e il 5xx è suo.
    const http = /^translate_http_(\d{3})$/.exec(codice);
    if (http) {
        const stato = Number(http[1]);
        return stato >= 400 && stato < 500
            ? `Il traduttore ha rifiutato la richiesta (HTTP ${stato}): di solito vuol dire che il ` +
                'modello per questa coppia di lingue non è installato sul contenitore.'
            : `Il traduttore ha risposto con un errore suo (HTTP ${stato}): tipicamente il servizio ` +
                'sta ripartendo. La riga resta in coda e si riprova.';
    }
    return codice;
}
/**
 * La lingua in cui scrivere a una persona: quella che ha scelto, se ha scelto; quella che
 * dice il gestionale, se non ha scelto; l'italiano se non lo dice nessuno.
 *
 * Le due colonne e il loro ordine sono la decisione di K73, e questa funzione è il punto
 * in cui quell'ordine diventa eseguibile. Sta qui e non sul server perché la stessa
 * precedenza serve anche alla app quando decide che cosa mostrare prima di aver parlato
 * col server: due punti che ordinano gli stessi due valori in modi diversi darebbero
 * un'app in cinese e una notifica in italiano, che è il guasto che le due colonne
 * esistono per non avere.
 *
 * `null` da tutte e due non è «italiano» in tabella: è «non lo so», e diventa italiano
 * **qui**, in un punto solo, dove si vede che la decisione è stata presa.
 */
export function linguaDiChiLegge(persona) {
    const scelta = persona.languageChosen;
    if (scelta !== null && LINGUE.includes(scelta))
        return scelta;
    const ereditata = persona.language;
    if (ereditata !== null && LINGUE.includes(ereditata)) {
        return ereditata;
    }
    return LINGUA_PREDEFINITA;
}
//# sourceMappingURL=traduzione.js.map